/**
 * Date & Time Utilities
 *
 * Contract v1 §1.3: the API sends UTC instants ('YYYY-MM-DDTHH:MM:SSZ') and
 * business rules run in America/Nassau. The site always shows times in the
 * business zone with the zone labelled, whatever the visitor's own zone is.
 * Nothing here guesses the browser's time zone.
 *
 * Slot instants are passed through untouched (slot.startISO is sent back as
 * start_time), so no date+time recombination happens on the client.
 */

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import config from '../../config';

dayjs.extend(utc);
dayjs.extend(timezone);

/** IANA zone all business times are shown in. */
export const BUSINESS_TIME_ZONE = config.business.timeZone;

/** Human label appended to displayed times, e.g. 'Bahamas time'. */
export const BUSINESS_TIME_ZONE_LABEL = config.business.timeZoneLabel;

const DATE_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;
// Offset required: 'Z' or ±HH:MM (contract §1.3 rejects naive datetimes).
const ISO_WITH_OFFSET_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;

/**
 * Parse a UTC/offset ISO instant into the business zone.
 *
 * Note: dayjs.tz(string, zone) would read the string's wall-clock as
 * Nassau time and ignore its 'Z', shifting every slot by the UTC offset.
 * Parsing the instant first and then converting is the correct order.
 *
 * @param {string} iso - ISO-8601 instant with offset
 * @returns {dayjs.Dayjs}
 */
export const toBusinessTime = (iso) => dayjs(iso).tz(BUSINESS_TIME_ZONE);

/**
 * @param {string} iso
 * @returns {boolean} True when `iso` is an ISO-8601 datetime carrying an offset.
 */
export const isIsoWithOffset = (iso) =>
  typeof iso === 'string' && ISO_WITH_OFFSET_RE.test(iso) && dayjs(iso).isValid();

/**
 * @param {string} date
 * @returns {boolean} True for a 'YYYY-MM-DD' string that is a real calendar date.
 */
export const isDateKey = (date) =>
  typeof date === 'string' && DATE_KEY_RE.test(date) && dayjs(date).format('YYYY-MM-DD') === date;

/**
 * Time of an instant in the business zone, labelled.
 * @param {string} iso
 * @returns {string} e.g. '9:30 AM Bahamas time'
 */
export const formatBusinessTime = (iso) => {
  if (!isIsoWithOffset(iso)) return '';
  return `${toBusinessTime(iso).format('h:mm A')} ${BUSINESS_TIME_ZONE_LABEL}`;
};

/**
 * Calendar date of an instant in the business zone.
 * @param {string} iso
 * @returns {string} e.g. 'Monday, October 5, 2026'
 */
export const formatBusinessDate = (iso) => {
  if (!isIsoWithOffset(iso)) return '';
  return toBusinessTime(iso).format('dddd, MMMM D, YYYY');
};

/**
 * Date and time of an instant in the business zone, labelled.
 * @param {string} iso
 * @returns {string} e.g. 'Monday, October 5, 2026 at 9:30 AM Bahamas time'
 */
export const formatBusinessDateTime = (iso) => {
  if (!isIsoWithOffset(iso)) return '';
  return `${formatBusinessDate(iso)} at ${formatBusinessTime(iso)}`;
};

/**
 * 'YYYY-MM-DD' key for a calendar day the visitor picked.
 *
 * A picker's Date object (new Date(y, m, d)) represents a calendar day, not
 * an instant; its local Y/M/D are the day the visitor clicked, which the API
 * reads as a Nassau calendar day (contract §1.3).
 *
 * @param {Date|string|dayjs.Dayjs} date
 * @returns {string}
 */
export const formatDate = (date) => {
  // Already a day key: return as-is (never roll '2026-02-30' over to March).
  if (typeof date === 'string' && DATE_KEY_RE.test(date)) return date;
  return dayjs(date).format('YYYY-MM-DD');
};

/**
 * Today's date in the business zone.
 * @returns {string} 'YYYY-MM-DD'
 */
export const getBusinessToday = () => dayjs().tz(BUSINESS_TIME_ZONE).format('YYYY-MM-DD');

/**
 * @param {string} dateKey - 'YYYY-MM-DD'
 * @returns {boolean} True when the day is before today in the business zone.
 */
export const isPastBusinessDate = (dateKey) => formatDate(dateKey) < getBusinessToday();
